import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  isWallDeviceLocked,
  hasWallSession,
  checkWallDeviceStatus,
  clearWallDeviceLock,
} from '@/lib/wall-device';
import { supabase } from '@/integrations/supabase/client';

/**
 * Top-level guard that enforces "Wall-Only Device" mode.
 *
 * If the local browser has been paired as a wall device:
 * - Any navigation to a route other than /dashboard (the wall) or /wall/unpair
 *   is immediately redirected back to /dashboard.
 * - We periodically ping `wall-device-status`. If the parent has revoked
 *   this device from their own phone, we clear the lock, sign the user out,
 *   and bounce to /auth so the device can be re-paired or repurposed.
 */
const ALLOWED_PATHS = new Set(['/dashboard', '/wall-login', '/wall/unpair']);

export default function WallDeviceGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const pollRef = useRef<number | null>(null);

  // Route enforcement
  useEffect(() => {
    if (!isWallDeviceLocked()) return;
    const path = location.pathname;
    if (!hasWallSession() && path !== '/wall-login') {
      navigate('/wall-login', { replace: true });
      return;
    }
    if (!ALLOWED_PATHS.has(path) && !path.startsWith('/wall/unpair')) {
      navigate('/dashboard', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Periodic revocation check
  useEffect(() => {
    if (!isWallDeviceLocked()) return;
    if (!hasWallSession()) return;

    const tick = async () => {
      const status = await checkWallDeviceStatus();
      if (status === 'revoked' || status === 'not_found') {
        clearWallDeviceLock();
        try { await supabase.auth.signOut(); } catch { /* */ }
        navigate('/auth', { replace: true });
      }
    };
    // Run once immediately, then every 60s
    tick();
    pollRef.current = window.setInterval(tick, 60_000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [navigate]);

  return null;
}
